import React, { useState } from 'react';
import { Modal } from '@/components/primitives/Modal';
import { Button } from '@/components/primitives/Button';
import { Icon } from '@/components/primitives/Icon';
import { createCampaign } from '@/services/campaigns';
import type { Campaign } from '@/types/api';

interface NewCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCampaignCreated?: (campaign: Campaign) => void;
}

export function NewCampaignModal({ isOpen, onClose, onCampaignCreated }: NewCampaignModalProps) {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('brand');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!name.trim()) {
      setError('Campaign name is required.');
      return;
    }
    setError('');
    setIsSubmitting(true);

    try {
      // Goal, StartDate, and EndDate are mocked here relative to standard API
      const campaign = await createCampaign({
        name: name.trim(),
      });

      if (onCampaignCreated) onCampaignCreated(campaign);

      // Complete & reset
      setName('');
      setGoal('brand');
      setStartDate('');
      setEndDate('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New Campaign"
      subtitle="Configure your basic campaign settings below."
      footer={
        <Button
          onClick={handleSave}
          disabled={isSubmitting || !name.trim()}
          className="aspectly:w-full aspectly:py-4 aspectly:text-base aspectly:shadow-lg aspectly:shadow-primary/20 aspectly:justify-center"
        >
          {isSubmitting ? 'Saving...' : 'Create'}
        </Button>
      }
    >
      <form id="new-campaign-form" onSubmit={handleSave} className="aspectly:space-y-8">
        {error && (
          <div className="aspectly:p-3 aspectly:rounded-lg aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
            {error}
          </div>
        )}
        <div className="aspectly:space-y-6">
          <div className="aspectly:space-y-2">
            <label
              className="aspectly:text-[11px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-on-surface-variant aspectly:block"
              htmlFor="name"
            >
              Campaign Name
            </label>
            <input
              className="aspectly:w-full aspectly:bg-surface-container-low aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:px-4 aspectly:py-4 aspectly:text-lg aspectly:font-bold aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/20 aspectly:focus:border-primary aspectly:transition-all aspectly:placeholder:text-on-surface-variant/40 aspectly:placeholder:font-normal"
              id="name"
              placeholder="Summer Collection 2024"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
          <div className="aspectly:space-y-2">
            <label
              className="aspectly:text-[11px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-on-surface-variant aspectly:block"
              htmlFor="goal"
            >
              Campaign Goal
            </label>
            <div className="aspectly:relative">
              <select
                className="aspectly:w-full aspectly:bg-surface-container-low aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:pl-4 aspectly:pr-10 aspectly:py-3 aspectly:text-sm aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/20 aspectly:focus:border-primary aspectly:transition-all aspectly:appearance-none aspectly:text-on-surface aspectly:font-medium aspectly:cursor-pointer aspectly:disabled:cursor-not-allowed"
                id="goal"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                disabled={isSubmitting}
              >
                <option value="brand">Brand Awareness</option>
                <option value="traffic">Website Traffic</option>
                <option value="leads">Lead Generation</option>
                <option value="sales">Sales Conversion</option>
              </select>
              <Icon
                name="expand_more"
                className="aspectly:absolute aspectly:right-3 aspectly:top-1/2 aspectly:-translate-y-1/2 aspectly:text-on-surface-variant aspectly:pointer-events-none"
              />
            </div>
          </div>
        </div>
        <div className="aspectly:pt-6 aspectly:border-t aspectly:border-outline-variant/10 aspectly:space-y-4">
          <h3 className="aspectly:text-[11px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-primary">
            Campaign Dates
          </h3>
          <div className="aspectly:grid aspectly:grid-cols-2 aspectly:gap-4">
            <div className="aspectly:space-y-1.5">
              <label
                className="aspectly:text-[10px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-on-surface-variant aspectly:block"
                htmlFor="start-date"
              >
                Launch Date
              </label>
              <div className="aspectly:relative">
                <Icon
                  name="calendar_today"
                  className="aspectly:absolute aspectly:left-3 aspectly:top-1/2 aspectly:-translate-y-1/2 aspectly:text-on-surface-variant/60 aspectly:text-sm aspectly:pointer-events-none"
                />
                <input
                  className="aspectly:w-full aspectly:bg-surface-container-low aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:pl-10 aspectly:pr-4 aspectly:py-3 aspectly:text-sm aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/20 aspectly:focus:border-primary aspectly:transition-all aspectly:text-on-surface"
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="aspectly:space-y-1.5">
              <label
                className="aspectly:text-[10px] aspectly:uppercase aspectly:font-black aspectly:tracking-widest aspectly:text-on-surface-variant aspectly:block"
                htmlFor="end-date"
              >
                Close Date
              </label>
              <div className="aspectly:relative">
                <Icon
                  name="event_busy"
                  className="aspectly:absolute aspectly:left-3 aspectly:top-1/2 aspectly:-translate-y-1/2 aspectly:text-on-surface-variant/60 aspectly:text-sm aspectly:pointer-events-none"
                />
                <input
                  className="aspectly:w-full aspectly:bg-surface-container-low aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:pl-10 aspectly:pr-4 aspectly:py-3 aspectly:text-sm aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/20 aspectly:focus:border-primary aspectly:transition-all aspectly:text-on-surface"
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}
